import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSearch, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';

function ProductList() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [productId, setProductId] = useState(null);

    const [productImage, setProductImage] = useState(null);

    const [categoryId, setCategoryId] = useState('');
    const [subcategoryId, setSubcategoryId] = useState('');

    const [imagePreview, setImagePreview] = useState(null);

    // ---------------------------------------------------------
    // FETCH PRODUCTS
    // ---------------------------------------------------------

    const fetchProducts = async () => {
        const token = localStorage.getItem('authToken');

        if (!token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/getproduct`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch products');
            }

            const result = await response.json();

            setProducts(result.products || []);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    // ---------------------------------------------------------
    // FETCH CATEGORIES
    // ---------------------------------------------------------

    const fetchCategories = async () => {
        const token = localStorage.getItem('authToken');

        if (!token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/getcategories`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch categories');
            }

            const result = await response.json();

            setCategories(result.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // ---------------------------------------------------------
    // FETCH SUBCATEGORIES BY CATEGORY
    // ---------------------------------------------------------

    const fetchSubcategories = async (selectedCategoryId) => {
        const token = localStorage.getItem('authToken');

        if (!token || !selectedCategoryId) {
            setSubcategories([]);
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/getsubcategory/category/${selectedCategoryId}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                // A category may simply have no subcategories
                setSubcategories([]);
                return;
            }

            const result = await response.json();

            setSubcategories(result.data || []);
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSubcategories([]);
        }
    };

    // ---------------------------------------------------------
    // INITIAL LOAD
    // ---------------------------------------------------------

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    // ---------------------------------------------------------
    // CATEGORY CHANGE
    // ---------------------------------------------------------

    const handleCategoryChange = async (e) => {
        const selectedCategoryId = e.target.value;

        setCategoryId(selectedCategoryId);

        // Whenever category changes, reset subcategory
        setSubcategoryId('');

        // Load subcategories for selected category
        await fetchSubcategories(selectedCategoryId);
    };

    // ---------------------------------------------------------
    // SEARCH
    // ---------------------------------------------------------

    const filteredProducts = products.filter((product) =>
        product &&
        Object.values(product).some((value) =>
            String(value)
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        )
    );

    // ---------------------------------------------------------
    // PAGINATION
    // ---------------------------------------------------------

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentProducts = filteredProducts.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

    const totalPages = Math.ceil(
        filteredProducts.length / itemsPerPage
    );

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // ---------------------------------------------------------
    // ADD / EDIT PRODUCT
    // ---------------------------------------------------------

    const handleAddOrEditProduct = async () => {
        const token = localStorage.getItem('authToken');

        if (!token) return;

        // Category is required
        if (!categoryId) {
            alert('Please select a category.');
            return;
        }

        // Image required while adding
        if (!isEditing && !productImage) {
            alert('Please select a product image.');
            return;
        }

        const formData = new FormData();

        // Always send category
        formData.append('category_id', categoryId);

        // Only send subcategory when one is selected
        // If category has no subcategory, backend can keep it NULL.
        if (subcategoryId) {
            formData.append('subcategory_id', subcategoryId);
        }

        // Image
        if (productImage) {
            formData.append('product_image', productImage);
        }

        try {
            const method = isEditing ? 'PUT' : 'POST';

            const url = isEditing
                ? `${process.env.REACT_APP_API_URL}/updateproducts/${productId}`
                : `${process.env.REACT_APP_API_URL}/addproduct`;

            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));

                throw new Error(
                    errorData.error ||
                    (isEditing
                        ? 'Failed to update product'
                        : 'Failed to add product')
                );
            }

            await response.json();

            // Refresh products
            await fetchProducts();

            setShowModal(false);
            resetForm();

        } catch (error) {
            console.error(
                'Error adding/updating product:',
                error
            );

            alert(error.message);
        }
    };

    // ---------------------------------------------------------
    // EDIT PRODUCT
    // ---------------------------------------------------------

    const handleEditClick = async (product) => {
        setProductId(product.product_id);

        // Set category
        setCategoryId(product.category_id);

        // Existing subcategory
        setSubcategoryId(product.subcategory_id || '');

        // No new image initially
        setProductImage(null);

        // Existing image preview
        setImagePreview(product.product_image || null);

        setIsEditing(true);
        setShowModal(true);

        // Load subcategories of existing category
        await fetchSubcategories(product.category_id);
    };

    // ---------------------------------------------------------
    // DELETE
    // ---------------------------------------------------------

    const handleDeleteClick = (productId) => {
        setProductId(productId);
        setShowDeleteModal(true);
    };

    const handleDeleteProduct = async () => {
        const token = localStorage.getItem('authToken');

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/deleteproducts/${productId}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete product');
            }

            await fetchProducts();

            setShowDeleteModal(false);
            setProductId(null);

        } catch (error) {
            console.error(
                'Error deleting product:',
                error
            );

            alert(error.message);
        }
    };

    // ---------------------------------------------------------
    // IMAGE CHANGE
    // ---------------------------------------------------------

    const handleImageChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            setProductImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // ---------------------------------------------------------
    // RESET FORM
    // ---------------------------------------------------------

    const resetForm = () => {
        setProductImage(null);

        setCategoryId('');
        setSubcategoryId('');

        setSubcategories([]);

        setImagePreview(null);

        setProductId(null);
        setIsEditing(false);
    };

    // ---------------------------------------------------------
    // OPEN ADD PRODUCT
    // ---------------------------------------------------------

    const handleAddProductClick = () => {
        resetForm();
        setShowModal(true);
    };

    // ---------------------------------------------------------
    // CLOSE MODAL
    // ---------------------------------------------------------

    const handleModalClose = () => {
        resetForm();
        setShowModal(false);
    };

    return (
        <div
            className="container"
            style={{ marginTop: '80px' }}
        >
            <h2 className="text-primary">
                Product List
            </h2>

            {/* Search + Add */}
            <div className="d-flex justify-content-between align-items-center mb-4">

                <div
                    className="input-group me-3"
                    style={{ maxWidth: '300px' }}
                >
                    <span className="input-group-text bg-primary text-white">
                        <FaSearch />
                    </span>

                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm(e.target.value)
                        }
                    />
                </div>

                <button
                    className="btn btn-success"
                    onClick={handleAddProductClick}
                >
                    <FaPlus /> Add Product
                </button>
            </div>

            {/* Product Table */}
            <table className="table table-bordered table-striped">

                <thead className="table-primary">
                    <tr>
                        <th>ID</th>
                        <th>Category</th>
                        <th>SubCategory</th>
                        <th>Image</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>

                <tbody>

                    {currentProducts.length > 0 ? (

                        currentProducts.map((product) => (

                            <tr key={product.product_id}>

                                <td>
                                    {product.product_id}
                                </td>

                                <td>
                                    {product.category_id || '-'}
                                </td>

                                <td>
                                    {product.subcategory_id || '-'}
                                </td>

                                <td>
                                    <img
                                        src={product.product_image}
                                        alt="Product"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </td>

                                <td>
                                    {product.product_createdAt
                                        ? new Date(
                                            product.product_createdAt
                                        ).toLocaleDateString()
                                        : '-'}
                                </td>

                                <td>

                                    <button
                                        className="btn btn-sm btn-outline-success me-2"
                                        onClick={() =>
                                            handleEditClick(product)
                                        }
                                    >
                                        <FaEdit /> Edit
                                    </button>

                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                            handleDeleteClick(
                                                product.product_id
                                            )
                                        }
                                    >
                                        <FaTrash /> Delete
                                    </button>

                                </td>

                            </tr>

                        ))

                    ) : (

                        <tr>
                            <td
                                colSpan="6"
                                className="text-center"
                            >
                                No products found
                            </td>
                        </tr>

                    )}

                </tbody>

            </table>

            {/* Pagination */}
            <nav>
                <ul className="pagination justify-content-center">

                    {[...Array(totalPages)].map(
                        (_, index) => (

                            <li
                                key={index}
                                className={`page-item ${
                                    currentPage === index + 1
                                        ? 'active'
                                        : ''
                                }`}
                            >

                                <button
                                    className="page-link"
                                    onClick={() =>
                                        handlePageChange(
                                            index + 1
                                        )
                                    }
                                >
                                    {index + 1}
                                </button>

                            </li>

                        )
                    )}

                </ul>
            </nav>

            {/* Add / Edit Modal */}
            <Modal
                show={showModal}
                onHide={handleModalClose}
            >

                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditing
                            ? 'Edit Product'
                            : 'Add Product'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    <Form>

                        {/* CATEGORY */}
                        <Form.Group className="mb-3">

                            <Form.Label>
                                Category
                            </Form.Label>

                            <Form.Select
                                value={categoryId}
                                onChange={handleCategoryChange}
                            >

                                <option value="">
                                    Select Category
                                </option>

                                {categories.map(
                                    (category) => (

                                        <option
                                            key={
                                                category.category_id
                                            }
                                            value={
                                                category.category_id
                                            }
                                        >
                                            {category.category}
                                        </option>

                                    )
                                )}

                            </Form.Select>

                        </Form.Group>

                        {/* SUBCATEGORY */}
                        {categoryId && (
                            <Form.Group className="mb-3">

                                <Form.Label>
                                    SubCategory
                                </Form.Label>

                                <Form.Select
                                    value={subcategoryId}
                                    onChange={(e) =>
                                        setSubcategoryId(
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        {subcategories.length > 0
                                            ? 'Select SubCategory'
                                            : 'No SubCategory'}
                                    </option>

                                    {subcategories.map(
                                        (subcategory) => (

                                            <option
                                                key={
                                                    subcategory.subcategory_id
                                                }
                                                value={
                                                    subcategory.subcategory_id
                                                }
                                            >
                                                {
                                                    subcategory.subcategory
                                                }
                                            </option>

                                        )
                                    )}

                                </Form.Select>

                                {subcategories.length === 0 && (
                                    <Form.Text className="text-muted">
                                        This category has no subcategory.
                                        The product will be saved directly
                                        under the main category.
                                    </Form.Text>
                                )}

                            </Form.Group>
                        )}

                        {/* PRODUCT IMAGE */}
                        <Form.Group className="mb-3">

                            <Form.Label>
                                Product Image
                            </Form.Label>

                            <Form.Control
                                type="file"
                                onChange={handleImageChange}
                                accept="image/*"
                            />

                            {imagePreview && (
                                <img
                                    src={imagePreview}
                                    alt="preview"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'cover',
                                        marginTop: '10px'
                                    }}
                                />
                            )}

                        </Form.Group>

                    </Form>

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={handleModalClose}
                    >
                        Close
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleAddOrEditProduct}
                    >
                        {isEditing
                            ? 'Update'
                            : 'Add'}
                    </Button>

                </Modal.Footer>

            </Modal>

            {/* Delete Confirmation */}
            <Modal
                show={showDeleteModal}
                onHide={() =>
                    setShowDeleteModal(false)
                }
            >

                <Modal.Header closeButton>
                    <Modal.Title>
                        Confirm Deletion
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Are you sure you want to delete this product?
                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setShowDeleteModal(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="danger"
                        onClick={handleDeleteProduct}
                    >
                        Delete
                    </Button>

                </Modal.Footer>

            </Modal>

        </div>
    );
}

export default ProductList;